import React, {useCallback, useMemo} from 'react';
import {Typography} from '@mui/material';
import {highlightText} from '../highlightText';
import {highlightEntitiesAndQuery} from '../highlightEntities';
import {positionHighlightLabelInFrame} from './highlightLabelPositioning';

// Memoized text segment component
const TextSegment = React.memo(({
                                    index,
                                    content,
                                    segmentData,
                                    highlightsBySegment,
                                    entitiesMap,
                                    effectiveQuery,
                                    onEntityClick,
                                }) => {
    const text = content || '';
    const len = text.length;

    // Get entities from on-demand fetch (by segmentId)
    const segmentId = segmentData?.segmentId;
    const entities = useMemo(() => {
        return segmentId ? (entitiesMap.get(segmentId) || []) : [];
    }, [entitiesMap, segmentId]);

    const rangesRaw = useMemo(() => {
        return (highlightsBySegment.get(index) || []).map(r => {
            let from;
            let to;
            if (r.startIndex === r.endIndex) {
                from = r.startOffset;
                to = r.endOffset;
            } else if (index === r.startIndex) {
                from = r.startOffset;
                to = len;
            } else if (index === r.endIndex) {
                from = 0;
                to = r.endOffset;
            } else {
                from = 0;
                to = len;
            }
            // clamp
            from = Math.max(0, Math.min(len, from));
            to = Math.max(0, Math.min(len, to));
            if (to < from) [from, to] = [to, from];
            return {from, to, type: r.type, id: r.id, labelText: r.labelText};
        }).filter(r => r.to > r.from);
    }, [highlightsBySegment, index, len]);

    const handleEnterOrFocus = useCallback((e) => {
        const hl = e.currentTarget;
        const frame = hl.closest?.('[id^="segment-"]') || hl.parentElement;
        positionHighlightLabelInFrame(hl, frame);
    }, []);

    const renderedContent = useMemo(() => {
        if (rangesRaw.length === 0) {
            // No citation/package highlights, just apply entity + query highlighting
            return highlightEntitiesAndQuery(text, entities, effectiveQuery, highlightText, onEntityClick);
        }

        // Determine unique boundaries and sweep to support overlapping types with priority
        const boundaries = Array.from(new Set([
            0,
            len,
            ...rangesRaw.flatMap(r => [r.from, r.to])
        ])).sort((a, b) => a - b);

        const typePriority = {cited: 2, sourcePackage: 1};

        const out = [];
        for (let i = 0; i < boundaries.length - 1; i++) {
            const a = boundaries[i];
            const b = boundaries[i + 1];
            if (b <= a) continue;
            const chunk = text.slice(a, b);

            // Which ranges cover [a,b)?
            const active = rangesRaw.filter(r => r.from < b && r.to > a);
            if (active.length === 0) {
                out.push(
                    <React.Fragment key={`t-${i}-n`}>
                        {highlightEntitiesAndQuery(chunk, entities, effectiveQuery, highlightText, onEntityClick, a)}
                    </React.Fragment>
                );
                continue;
            }

            // Pick highest priority type and its label
            let chosenType = active[0].type;
            for (const r of active) {
                if ((typePriority[r.type] || 0) > (typePriority[chosenType] || 0)) chosenType = r.type;
            }
            const chosenItem = active.find(r => r.type === chosenType);
            const labelText = chosenItem?.labelText || (chosenType === 'cited' ? 'Cited' : 'In package');

            out.push(
                <span
                    key={`t-${i}-h`}
                    className={`hl hl-${chosenType}`}
                    data-label={labelText}
                    onMouseEnter={handleEnterOrFocus}
                    onFocus={handleEnterOrFocus}
                    tabIndex={0}
                >
                    <span className="hl-label" role="tooltip">{labelText}</span>
                    {highlightEntitiesAndQuery(chunk, entities, effectiveQuery, highlightText, onEntityClick, a)}
                </span>
            );
        }

        return out;
    }, [text, rangesRaw, entities, effectiveQuery, onEntityClick, len, handleEnterOrFocus]);

    return (
        <Typography variant={'body1'} id={`segment-${index}`} spacing={1} p={1}>
            {renderedContent}
        </Typography>
    );
});

export default TextSegment;

